/**
 * httpProgressBoot — regista os hooks globais que alimentam o TopProgressBar.
 *
 * Deve ser chamado UMA vez, no boot da app (index.js), antes de renderizar
 * qualquer componente que faça requests.
 *
 * O que faz:
 *   1. Regista `axios.interceptors.request` e `response` para incrementar/
 *      decrementar o contador `pending`.
 *   2. Faz monkey-patch de `window.fetch` para o mesmo efeito.
 *   3. Ignora URLs "noise" (Google Analytics, hot reload, service-worker
 *      background sync) para não spammear a barra.
 */
import axios from 'axios';
import { start, done } from './httpProgress';

const IGNORE_URL_PATTERNS = [
  /google-analytics\.com/i,
  /googletagmanager\.com/i,
  /\/service-worker\.js/i,
  /\/sockjs-node\//i,
  /hot-update/i,
  /\/__webpack_hmr/i,
  // Health check silencioso (não queremos flicker de fundo)
  /\/api\/health$/i,
  /\/api\/_version$/i,
];

const shouldIgnore = (url) => {
  if (!url || typeof url !== 'string') return false;
  return IGNORE_URL_PATTERNS.some((rx) => rx.test(url));
};

let installed = false;

export function installHttpProgress() {
  if (installed) return;
  installed = true;

  // 1. axios
  try {
    axios.interceptors.request.use(
      (config) => {
        if (!shouldIgnore(config?.url || '')) {
          // Marca este request para não fazer done duplo se outro interceptor
          // se meter no meio.
          // eslint-disable-next-line no-param-reassign
          config.__httpProgressStarted = true;
          start();
        }
        return config;
      },
      (err) => {
        // Se falhou antes de sair, não incrementámos — nada a fazer.
        return Promise.reject(err);
      },
    );
    axios.interceptors.response.use(
      (res) => {
        if (res?.config?.__httpProgressStarted) done();
        return res;
      },
      (err) => {
        if (err?.config?.__httpProgressStarted) done();
        return Promise.reject(err);
      },
    );
  } catch (_) { /* axios não disponível — ignora */ }

  // 2. window.fetch
  try {
    if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
      const orig = window.fetch.bind(window);
      window.fetch = async (input, init) => {
        const url = typeof input === 'string' ? input : (input?.url || '');
        const track = !shouldIgnore(url);
        if (track) start();
        try {
          const res = await orig(input, init);
          return res;
        } finally {
          if (track) done();
        }
      };
    }
  } catch (_) { /* ignora */ }
}
